import React, { forwardRef, ReactElement, useEffect, useImperativeHandle, useMemo } from 'react';
import { Box, ClickAwayListener, Popper, type PopperPlacementType, type SxProps, type Theme } from '@mui/material';
import { bindPopper, bindTrigger, type PopupState, usePopupState } from 'material-ui-popup-state/hooks';
import { mergeSx } from 'merge-sx';

import { PositionType, useDirection } from '@/hooks/useDirection';
import { transforms } from '@/theme/constants';
import shadows from '@/theme/shadows';

const CARET_SIZE = 1.5;

interface StandardPopperProps {
  children: ReactElement | ((state: PopupState) => ReactElement);
  content: ReactElement;
  getTriggerSx?: (isOpen: boolean) => SxProps<Theme>;
  getSx?: (isOpen: boolean) => SxProps<Theme>;
  caretEndPosition?: number | string;
  caretStartPosition?: number | string;
  placement?: PopperPlacementType;
  noTrigger?: boolean;
  onToggle?: (isOpen: boolean) => void;
  treeRootSelector?: string;
  hoverable?: boolean;
}

const getCaretDefaultSX = ({
  start,
  end,
  isCaretStartPosition,
  caretEndPosition,
  caretStartPosition
}: {
  start: PositionType;
  end: PositionType;
  isCaretStartPosition: boolean;
  caretEndPosition?: number | string;
  caretStartPosition?: number | string;
}) => ({
  content: '""',
  display: 'block',
  position: 'absolute',
  [start]: isCaretStartPosition ? caretStartPosition || '50%' : null,
  [end]: isCaretStartPosition ? null : caretEndPosition || '50%',
  width: ({ spacing }: Theme) => spacing(CARET_SIZE),
  height: ({ spacing }: Theme) => spacing(CARET_SIZE)
});

const getCaretPositionSX = (translateXBackward: boolean, isTop: boolean) => ({
  transform: transforms.compose(
    transforms.shift50Y(!isTop),
    transforms.shift50X(translateXBackward),
    transforms.rotate45
  ),
  ...(isTop && {
    bottom: 0
  }),
  ...(!isTop && {
    top: 0
  }),
  borderColor: 'grey.400'
});

const StandardPopper = forwardRef(
  (
    {
      children,
      content,
      getTriggerSx,
      getSx,
      caretEndPosition,
      caretStartPosition,
      placement,
      noTrigger,
      onToggle,
      treeRootSelector = '#root',
      hoverable = false
    }: StandardPopperProps,
    ref
  ) => {
    const { start, end, isRtl } = useDirection();
    const uniqueId = useMemo(() => `popper-${crypto.randomUUID()}`, []);
    const popupState = usePopupState({ variant: 'popper', popupId: uniqueId });

    useImperativeHandle(
      ref,
      () => ({
        close: popupState.close,
        open: popupState.open,
        toggle: popupState.toggle
      }),
      [popupState]
    );

    useEffect(() => {
      onToggle?.(popupState.isOpen);
    }, [popupState.isOpen, onToggle]);

    if (caretEndPosition && caretStartPosition) {
      throw new Error('Cannot specify both caret start position and caret end position');
    }

    const isCaretStartPosition = !caretEndPosition;
    const translateXBackward = isCaretStartPosition !== isRtl;

    const caretDefaultSx = useMemo(
      () =>
        getCaretDefaultSX({
          start,
          end,
          isCaretStartPosition,
          caretEndPosition,
          caretStartPosition
        }),
      [start, end, isCaretStartPosition, caretEndPosition, caretStartPosition]
    );

    return (
      <>
        <Box
          {...(noTrigger ? null : bindTrigger(popupState))}
          onMouseEnter={hoverable ? popupState.open : undefined}
          onMouseLeave={hoverable ? popupState.close : undefined}
          sx={mergeSx<Theme>(
            {
              height: '100%',
              cursor: noTrigger ? 'default' : 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            },
            getTriggerSx?.(popupState.isOpen)
          )}
        >
          {typeof children === 'function' ? children(popupState) : children}
        </Box>
        <Popper
          {...bindPopper(popupState)}
          placement={placement || 'bottom'}
          className="StandardPopper-root"
          onMouseEnter={hoverable ? popupState.open : undefined}
          onMouseLeave={hoverable ? popupState.close : undefined}
          sx={mergeSx<Theme>(
            {
              zIndex: 10,
              '& .MuiPaper-root': {
                bgcolor: 'primary.50',
                position: 'relative',
                '&::before': {
                  ...caretDefaultSx,
                  zIndex: -1,
                  boxShadow: shadows[1]
                },
                '&::after': {
                  ...caretDefaultSx,
                  bgcolor: 'inherit',
                  zIndex: 0
                }
              },

              [[
                '&[data-popper-placement="top"] .MuiPaper-root::before',
                '&[data-popper-placement="top-start"] .MuiPaper-root::before',
                '&[data-popper-placement="top-end"] .MuiPaper-root::before',
                '&[data-popper-placement="top"] .MuiPaper-root::after',
                '&[data-popper-placement="top-start"] .MuiPaper-root::after',
                '&[data-popper-placement="top-end"] .MuiPaper-root::after'
              ].join(', ')]: getCaretPositionSX(translateXBackward, true),
              [[
                '&[data-popper-placement="bottom"] .MuiPaper-root::before',
                '&[data-popper-placement="bottom-start"] .MuiPaper-root::before',
                '&[data-popper-placement="bottom-end"] .MuiPaper-root::before',
                '&[data-popper-placement="bottom"] .MuiPaper-root::after',
                '&[data-popper-placement="bottom-start"] .MuiPaper-root::after',
                '&[data-popper-placement="bottom-end"] .MuiPaper-root::after'
              ].join(', ')]: getCaretPositionSX(translateXBackward, false),
              [[
                '&[data-popper-placement="top"] .MuiPaper-root',
                '&[data-popper-placement="top-start"] .MuiPaper-root',
                '&[data-popper-placement="top-end"] .MuiPaper-root'
              ].join(', ')]: { mb: 1 },
              [[
                '&[data-popper-placement="bottom"] .MuiPaper-root',
                '&[data-popper-placement="bottom-start"] .MuiPaper-root',
                '&[data-popper-placement="bottom-end"] .MuiPaper-root'
              ].join(', ')]: { mt: 1 }
            },
            getSx?.(popupState.isOpen)
          )}
        >
          <ClickAwayListener
            onClickAway={event => {
              if ((event?.target as HTMLElement)?.closest(treeRootSelector)) {
                popupState.close();
              }
            }}
          >
            {content}
          </ClickAwayListener>
        </Popper>
      </>
    );
  }
);

StandardPopper.displayName = 'StandardPopper';

export default StandardPopper;
